import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { siteDelete } from '../../../redux/api/siteAPI';
import { setUserSite } from '../../../redux/actions/authActions';
import Input from '../../ui/input';
import Button from '../../ui/button';

import { useTranslation } from 'react-i18next';

const DeleteSiteModal = ({ isOpen, onClose, data }) => {
    const { t } = useTranslation('Sites');
    const dispatch = useDispatch();
    const userSite = useSelector((state) => state.auth?.site);
    const [error, setError] = useState();
    const [deleteMSG, setDeleteMSG] = useState("");
    const [MSGError, setMSGError] = useState("");
    const [loading, setLoading] = useState(false);

    const closeModal = (refresh = false) => {
        if (loading) return;
        onClose(refresh);
    };

    const handleChange = (e) => {
        e.preventDefault();
        setMSGError("");
        setError(null);
        setDeleteMSG(e.target.value);
    }

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            const rawItems = Array.isArray(data) ? data : [data];
            const sitesToDelete = rawItems
                .map(item => (typeof item === 'object' && item !== null ? item.Site_ID : item))
                .filter(id => id !== undefined && id !== null);
            
            if (!Array.isArray(data) && data) {
                const msg = `delete ${data.Name}`.toLowerCase().replace(/\s/g, "");
                if (msg !== deleteMSG.toLowerCase().replace(/\s/g, "")) {
                    setMSGError("Confirmation message does not match.");
                    setLoading(false);
                    return;
                }
            }

            const isBulkDelete = sitesToDelete.length > 1;

            const results = await Promise.all(
                sitesToDelete.map(id => siteDelete(id, { skipSuccessToast: isBulkDelete }))
            );

            const errors = results
                .filter(result => result.error)
                .map(result => result.error);

            const successCount = sitesToDelete.length - errors.length;
            if (isBulkDelete && successCount > 0) {
                toast.success(`${successCount} site(s) successfully deleted.`);
            }

            let storedSite = null;
            try {
                storedSite = JSON.parse(localStorage.getItem('site'));
            } catch {
                storedSite = null;
            }

            const currentDefaultId = userSite?.value ?? userSite?.Site_ID ?? storedSite?.value ?? storedSite?.Site_ID;

            const checkAndClearDefaultSite = () => {
                if (currentDefaultId !== null && currentDefaultId !== undefined && currentDefaultId !== "") {
                    const deletedSuccessfully = sitesToDelete.filter((_, index) => !results[index]?.error);
                    const isDefaultDeleted = deletedSuccessfully.some(
                        id => String(id) === String(currentDefaultId)
                    );
                    if (isDefaultDeleted) {
                        localStorage.removeItem('site');
                        localStorage.setItem('site', JSON.stringify({ label: null, value: null }));
                        dispatch(setUserSite({ label: null, value: null }));
                    }
                }
            };

            if (errors.length > 0) {
                setError(errors.join(', '));
                if (errors.length < sitesToDelete.length) {
                    checkAndClearDefaultSite();
                    onClose(true);
                }
            } else {
                checkAndClearDefaultSite();
                closeModal(true);
            }
        } catch (err) {
            console.error("Error Deleting Site(s):", err);
            setError("An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    const isBulk = Array.isArray(data);
    const count = isBulk ? data.length : 1;
    const siteName = !isBulk && data ? data.Name : '';

    return (
        <div className="grid space-y-4 w-auto m-2 bg-white p-8 rounded-3xl shadow-lg">
            <p className="text-center text-xl text-[#4C4C66] font-bold">{t('delete_confirmation')}</p>
            <p className="text-center text-md text-[#4C4C66] font-normal">
                {isBulk
                    ? `Are you sure you want to delete ${count} sites?`
                    : `Type "delete ${siteName}" to confirm.`
                }
            </p>
            {!isBulk && (
                <Input
                    data-testid="site-delete-confirmation"
                    type="text"
                    placeholder={`delete ${siteName}`}
                    value={deleteMSG}
                    onChange={handleChange}
                    className="w-full"
                />
            )}
            {MSGError && <p className="text-center text-red-500">{MSGError}</p>}
            {error && (
                <p className="text-center text-red-500 bg-red-100 p-2 rounded-md">{error}</p>
            )}
            <div className='flex justify-center pt-4 space-x-5'>
                <Button onClick={() => closeModal()} size="large" color="cancel" disabled={loading}>
                    {t('common:cancel')}
                </Button>
                <Button data-testid="site-delete-submit" onClick={handleSubmit} type="submit" size="large" disabled={loading}>
                    {loading ? t('common:deleting') : t('common:delete')}
                </Button>
            </div>
        </div>
    );
};

export default DeleteSiteModal;
